import { Address } from "company/detail/components/models";
import { Status, StatusDropdown } from "components";
import FormattedAddress from "components/Atoms/FormattedAddress";
import UserWithAvatar from "components/Atoms/UserWithAvatar";
import CultureLabel from "components/Culture/CultureLabel";
import { DeletedRow } from "components/DetailPage/DeletedRow";
import LocalTimeRow from "components/DetailPage/LocalTimeRow";
import NotValidatedRow from "components/DetailPage/NotValidatedRow";
import PhoneRow from "components/DetailPage/PhoneRow";
import CulturesTemplateDropdown from "components/dropdowns/reactselect/CulturesTemplateDropdown";
import { FormSection } from "components/MainSectionTemplate/types";
import { convertToPhoneNumbers, PhoneInput } from "components/PhoneInput";
import UserModalPicker from "components/Picker/UserPicker/UserModalPicker";
import { useHasMultipleStatuses } from "components/Status/hooks";
import { useHasAnyTags } from "components/Tag/hooks";
import TagList from "components/Tag/TagList";
import TagsSelect from "components/Tag/TagsSelect";
import UploadAvatar from "components/UploadAvatar/UploadAvatar";
import { AddressInnerForm } from "components/ValidatedFields/AddressInnerForm";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import ContactEmailRow from "contact/detail/components/ContactEmailRow";
import { ContactName } from "contact/detail/components/ContactName";
import { EmailWithBlacklist } from "contact/detail/components/EmailWithBlacklist";
import { GenderRadio } from "contact/detail/components/GenderRadio";
import { ContactFormState } from "contact/models";
import { getInstruction } from "contact/utils";
import React from "react";
import { Message } from "semantic-ui-react";
import { isNullOrEmptyString } from "shared/utils";
import { getCultureFromLcid } from "utilities/enumutils";
import { getGenderName } from "utilities/genderUtils";

export const contactMainSection = (
  contact: server.dto.Contact,
  formState: ContactFormState,
  tags: server.dto.TagSmall[],
  profileImageUrl: string | undefined,
  error: string | undefined,
  onSave: (file: File) => void
): FormSection => {
  const hasMultipleStatuses = useHasMultipleStatuses("contact");
  const hasAnyTags = useHasAnyTags("contact");

  return {
    key: "contactMainDetails",
    type: "regular",
    title: RESX.Contact.resxContact,
    fields: [{
      key: "deleted",
      label: RESX.File.Deleted,
      hidden: !contact.isDeleted,
      render: () => <DeletedRow />,
    }, {
      key: "isValidated",
      label: RESX.Planning.ExternalInput,
      render: () => <NotValidatedRow
        canApprove={contact.permissions.canModify}
        sublabel={RESX.Planning.InputFromWebform}
        approveEndpoint={() => window.CoreApi.Contact.Validate({
          contactId: contact.contactId
        })}
      />,
      hidden: contact.isValidated,
    }, {
      key: "name",
      label: RESX.GeneralLabels.Name,
      render: () => <div>{[formState.firstName, formState.insertion, formState.lastName].filter(Boolean).join(" ")}</div>,
      inPageEdit: {
        initialValue: [formState.firstName, formState.insertion, formState.lastName],
        render: ({ value, onChange, submit, cancel }) =>
          <ContactName
            focusOnMount
            firstName={value[0]}
            insertion={value[1]}
            lastName={value[2]}
            onChange={(firstName, insertion, lastName) => onChange([firstName, insertion, lastName])}
            onEnter={submit}
            onEsc={cancel}
            width={130}
          />,
        submitChange: ([firstName, insertion, lastName]: string[]) => {
          const instruction = getInstruction(contact, formState, {
            name: {
              first: firstName,
              insertion: insertion,
              last: lastName,
            }
          });
          return window.CoreApi.Contact.UpdateDetails(instruction)
        },
        type: "confirmOrCancelButton"
      }
    }, {
      key: "emailAddresses",
      label: RESX.GeneralLabels.Email,
      isEmpty: isNullOrEmptyString(formState.emailAddresses),
      render: () => <ContactEmailRow
        emailAddresses={formState.emailAddresses}
        emailBlacklisted={formState.isEmailBlacklisted}
      />,
      inPageEdit: {
        initialValue: [formState.emailAddresses, formState.isEmailBlacklisted],
        render: ({ value, onChange, submit, cancel }) => <EmailWithBlacklist
          value={value}
          onChange={v => onChange(v)}
          onEnter={submit}
          onEsc={cancel}
        />,
        fullWidth: true,
        submitChange: (value: [string, boolean]) => {
          const instruction = getInstruction(contact, formState, { emailAddresses: value[0], emailBlacklisted: value[1] });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        },
        type: "confirmOrCancelButton"
      }
    },
    {
      key: "mobileNumbers",
      label: RESX.Contact.Mobile,
      render: () => <PhoneRow numbers={convertToPhoneNumbers(formState.mobileNumbers)} />,
      isEmpty: isNullOrEmptyString(formState.mobileNumbers),
      inPageEdit: {
        initialValue: formState.mobileNumbers,
        render: ({ value, onChange, submit, cancel }) => <PhoneInput
          focusOnMount
          value={value}
          onChange={onChange}
          onEnter={submit}
          onEsc={cancel}
        />,
        fullWidth: true,
        type: "confirmOrCancelButton",
        submitChange: (value: string) => {
          const instruction = getInstruction(contact, formState, { mobileNumbers: convertToPhoneNumbers(value) });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        }
      }
    }, {
      key: "landline",
      label: RESX.ContactDetails.PhoneNumber,
      render: () => <PhoneRow numbers={convertToPhoneNumbers(formState.landlineNumbers)} />,
      isEmpty: isNullOrEmptyString(formState.landlineNumbers),
      inPageEdit: {
        initialValue: formState.landlineNumbers,
        fullWidth: true,
        render: ({ value, onChange, submit, cancel }) => <PhoneInput
          focusOnMount
          value={value}
          onChange={onChange}
          onEnter={submit}
          onEsc={cancel}
        />,
        type: "confirmOrCancelButton",
        submitChange: (value: string) => {
          const instruction = getInstruction(contact, formState, { landlineNumbers: convertToPhoneNumbers(value) });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        }
      }
    }, {
      key: "language",
      label: RESX.Language.resxLanguage,
      render: () => <CultureLabel culture={getCultureFromLcid(formState.culture)} />,
      inPageEdit: {
        initialValue: formState.culture,
        fullWidth: true,
        render: ({ value, onChange }) => (
          <CulturesTemplateDropdown
            value={value}
            onChange={c => onChange(c, true)}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true
            }}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: (value: server.dto.LCID) => {
          const instruction = getInstruction(contact, formState, { culture: value });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        }
      }
    }, {
      key: "gender",
      label: RESX.Contact.Gender,
      render: () => getGenderName(formState.gender),
      isEmpty: formState.gender === "unspecified",
      inPageEdit: {
        initialValue: formState.gender,
        render: ({ value, onChange }) => <div className="main-section-template-fields-padding">
          <GenderRadio
            value={value}
            onChange={v => onChange(v, true)}
          />
        </div>,
        type: "custom",
        submitChange: (value: server.dto.Genders) => {
          const instruction = getInstruction(contact, formState, { gender: value });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        }

      }
    }, {
      key: "position",
      label: RESX.Contact.Position,
      render: () => <div>{formState.position}</div>,
      isEmpty: isNullOrEmptyString(formState.position),
      inPageEdit: {
        initialValue: formState.position,
        render: ({ value, onChange, submit, cancel }) =>
          <OptionalTextInput
            value={value}
            onChange={onChange}
            onEnter={submit}
            onEsc={cancel}
            maxLength={serverReference.constants.contact.maxPositionLength}
            focusOnMount
          />,
        fullWidth: true,
        submitChange: (value: string) => {
          const instruction = getInstruction(contact, formState, { position: value });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        },
        type: "confirmOrCancelButton"
      }
    }, {
      key: "department",
      label: RESX.Contact.Department,
      render: () => <div>{formState.department}</div>,
      isEmpty: !formState.department,
      inPageEdit: {
        initialValue: formState.department,
        render: ({ value, onChange, submit, cancel }) =>
          <OptionalTextInput
            value={value}
            onChange={onChange}
            onEnter={submit}
            onEsc={cancel}
            maxLength={serverReference.constants.contact.maxDepartmentLength}
            focusOnMount
          />,
        fullWidth: true,
        submitChange: (value: string) => {
          const instruction = getInstruction(contact, formState, { department: value });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        },
        type: "confirmOrCancelButton"
      }
    }, {
      key: "tags",
      label: RESX.Tag.Tags,
      render: () => <TagList tags={tags} length="long" />,
      isEmpty: tags.length === 0,
      hidden: !hasAnyTags,
      inPageEdit: {
        render: ({ value, onChange }) => (
          <TagsSelect
            mainArea="contact"
            value={value}
            onChange={onChange}
            inPageEditing="edit"
          />
        ),
        type: "confirmOrCancelButton",
        initialValue: tags,
        submitChange: (tags: server.dto.TagSmall[]) => {
          return window.CoreApi.Contact.UpdateTags({ itemId: contact.contactId, tagsIds: tags.map(tag => tag.tagId) });
        }
      }
    }, {
      key: "profileImage",
      label: RESX.ESignLanding.btnImage,
      render: () => <div className="flex-column gap">
        <UploadAvatar avatarUrl={profileImageUrl} onSave={onSave} />
        {error && <Message color="red">{error}</Message>}
      </div>
    }, {
      key: "salutation",
      label: RESX.Contact.Salutation,
      render: () => formState.salutation,
      isEmpty: isNullOrEmptyString(formState.salutation),
      inPageEdit: {
        initialValue: formState.salutation,
        render: ({ value, onChange, submit, cancel }) =>
          <OptionalTextInput
            value={value}
            onChange={onChange}
            onEnter={submit}
            onEsc={cancel}
            maxLength={serverReference.constants.contact.maxSalutationLength}
            focusOnMount
          />,
        fullWidth: true,
        submitChange: (value: string) => {
          const instruction = getInstruction(contact, formState, { salutation: value });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        },
        type: "confirmOrCancelButton"
      }
    }, {
      key: "attn",
      label: RESX.Contact.AttnLineOnEnvelope,
      render: () => formState.attn,
      isEmpty: isNullOrEmptyString(formState.attn),
      inPageEdit: {
        initialValue: formState.attn,
        render: ({ value, onChange, submit, cancel }) =>
          <OptionalTextInput
            value={value}
            onChange={onChange}
            onEnter={submit}
            onEsc={cancel}
            maxLength={serverReference.constants.contact.maxAttnLength}
            placeholder={RESX.Contact.AttnLineOnEnvelopeDesc}
            focusOnMount
          />,
        fullWidth: true,
        submitChange: (value: string) => {
          const instruction = getInstruction(contact, formState, { attn: value });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        },
        type: "confirmOrCancelButton"
      }
    }, {
      key: "homeAddress",
      label: RESX.Contact.HomeAddress,
      render: () => <FormattedAddress address={contact.address} />,
      isEmpty: !contact.address.formatted?.length,
      inPageEdit: {
        initialValue: formState.homeAddress,
        useCustomSavingState: true,
        fullWidth: true,
        render: ({ value, onChange, submit, isSaving }) => (
          <AddressInnerForm
            address={value}
            onChange={onChange}
            submit={submit}
            isSaving={isSaving}
          />
        ),
        type: "custom",
        submitChange: (value: Address) => {
          const instruction = getInstruction(contact, formState, {
            address: {
              addressLine1: value.addressLine1,
              addressLine2: value.addressLine2,
              postalCode: value.postalCode,
              city: value.city,
              stateId: value.state.key
            }
          });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        }
      }

    }, {
      key: "localTime",
      label: RESX.DateTime.LocalTime,
      render: () => <LocalTimeRow ianaTimeZoneId={contact.ianaTimeZoneId} />,
      isEmpty: !contact.ianaTimeZoneId,
    }, {
      key: "status",
      label: RESX.Status.resxStatus,
      render: () => <Status status={formState.status} />,
      hidden: !hasMultipleStatuses,
      inPageEdit: {
        render: ({ value, onChange }) => (
          <StatusDropdown
            value={value}
            onChange={onChange}
            mainArea="contact"
            options={undefined}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true
            }}
          />
        ),
        type: "confirmOrCancelButton",
        initialValue: formState.status,
        fullWidth: true,
        submitChange: (value: server.dto.StatusSmall) => {
          const instruction = getInstruction(contact, formState, { statusId: value.statusId });
          return window.CoreApi.Contact.UpdateDetails(instruction);
        }
      }
    }, {
      key: "owner",
      label: RESX.GeneralLabels.Owner,
      render: () => <UserWithAvatar user={formState.owner} />,
      isEmpty: !formState.owner,
      inPageEdit: {
        hoverEdit: true,
        render: ({ onChange, cancel, value }) => (
          <div className="main-section-template-fields-padding">
            <UserModalPicker
              inPageEditing={{ onChange, cancel, hasValue: Boolean(value) }}
              canCreateNew={false}
            />
          </div>
        ),
        type: "custom",
        initialValue: formState.owner,
        submitChange: (value: server.dto.UserSmall) => {
          const instruction = getInstruction(contact, formState, {
            ownerUserId: value.userId,
          });

          return window.CoreApi.Contact.UpdateDetails(instruction);
        }
      }
    }]
  }
};